import { useNavigate } from 'react-router-dom'
import { LogOut, Menu, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet'
import { useAuthStore } from '@/stores/auth'
import { Sidebar } from './Sidebar'
import { ThemeToggle } from './ThemeToggle'

/**
 * Top app bar with mobile sidebar trigger + user menu.
 *
 * Story 1.7 §AC #13: the user-menu Logout entry calls `useAuthStore.clearAuth()`
 * to wipe the persisted JWT and navigates back to `/login`. Pure client-side
 * action — there is no server-side token revocation in the MVP (re-login is
 * the only refresh path).
 */
export function Topbar(): React.ReactElement {
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const navigate = useNavigate()

  const initial = (user?.name?.[0] ?? user?.email?.[0] ?? 'U').toUpperCase()

  const handleLogout = (): void => {
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <header className="flex h-16 items-center justify-between border-b bg-background px-4 md:px-6">
      <div className="flex items-center gap-3">
        {/* Mobile sidebar trigger */}
        <Sheet>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label="Abrir menu de navegação"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SheetTitle className="sr-only">Navegação</SheetTitle>
            {/* Re-use the same Sidebar inside the sheet for parity */}
            <div className="flex h-full">
              <div className="flex w-full flex-col">
                <div className="flex h-16 items-center border-b px-6">
                  <span className="text-lg font-semibold">SDR Agend</span>
                </div>
                <Sidebar />
              </div>
            </div>
          </SheetContent>
        </Sheet>
        <span className="text-sm text-muted-foreground">Painel Administrativo</span>
      </div>

      <div className="flex items-center gap-3">
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Abrir menu do usuário"
              className="rounded-full"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{user?.name ?? 'Usuário'}</span>
                <span className="text-xs text-muted-foreground">{user?.email ?? ''}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>Perfil</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>Sair</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
